import type { ReactElement } from 'react';
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';

import MFAWrapper from 'Components/MFAWrapper/MFAWrapper';
import type { MFAWrapperProps } from 'Components/MFAWrapper/MFAWrapper.types';
import PageBase from 'Components/shared/PageBase/PageBase';
import { createBrowserOnlyRedirectPath } from 'Utilities/routes.helpers';
import type { GetMainRouteSegment } from 'Utilities/routes.types';

type SupportedOriginKeys = GetMainRouteSegment<'SELLER_DETAILS' | 'DEALER_WALLET' | 'WITHDRAW_FUNDS'>;
type SupportedOrigins = Required<MFAWrapperProps>['logs']['origin'];

const SUPPORTED_ORIGINS: Record<SupportedOriginKeys, SupportedOrigins> = {
	sellerdetails: 'otp_setup_seller_details',
	wallet: 'otp_setup_wallet',
	'withdraw-funds': 'otp_setup_withdrawals',
};

const ORIGINS_REGEX = new RegExp(`(${Object.keys(SUPPORTED_ORIGINS).join('|')})`);

const getOrigin = (path: string): SupportedOrigins => {
	const match = ORIGINS_REGEX.exec(path);

	if (match) {
		return SUPPORTED_ORIGINS[match[1] as SupportedOriginKeys];
	}

	return 'otp_setup_shown';
};

// eslint-disable-next-line local-rules/boolean-naming
const IS_FULL_SCREEN = true;

const ConfirmIdentityPage = (): ReactElement => {
	const router = useRouter();

	// Remove ssr from query params - a check to prevent direct access to this page
	useEffect(() => {
		const { ssr: fromSSR, ...query } = router.query;

		if (fromSSR) {
			void router.replace({ query });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.query]);

	const onBackClick = () => {
		// We cannot use `router.replace` because `router.query.from` is the previous
		// page's URL as it appears to users, and the previous page might have
		// masked its real href (e.g. with the `as` parameter in `router.push`).
		window?.location?.replace(createBrowserOnlyRedirectPath(router.query.from));
	};

	const onSuccess = () => {
		void router.replace(createBrowserOnlyRedirectPath(router.query.to));
	};

	return (
		<PageBase
			description="metaTags.description"
			showSitewideFooter={!IS_FULL_SCREEN}
			title="metaTags.pages.confirmIdentity.title"
		>
			<MFAWrapper
				fullScreen={IS_FULL_SCREEN}
				logs={{
					origin: getOrigin(String(router.query.to)),
					source: 'accessSensitiveData',
				}}
				onBackClick={onBackClick}
				onSuccess={onSuccess}
			/>
		</PageBase>
	);
};

export default ConfirmIdentityPage;
